// src/components/layouts/LayoutIndex.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { lazy, memo, Suspense, useEffect, useRef, useContext } from 'react';
import SimpleLoadingPage from 'components/SimpleComponents/SimpleLoadingPage';
import SimpleLoadingModule from 'components/SimpleComponents/SimpleLoadingModule';
import app, { printGlobalStateToConsole } from 'configs/ConfigIndex';
import authService from 'services/authService';
import { useModuleStore } from 'stores/module';
import useZustand from 'hooks/useZustand';
import { languageContextReload } from 'lang/languageContextReload';
import { readInviteHashFromLocation, loadAndValidateInvite } from 'utils/crypto';
import { setStorageAuthItem } from 'stores/auth';
import { Watermark } from 'antd';

const AuthRoutes = lazy(() => import('routes/AuthRoutes'));
const AppRoutes = lazy(() => import('routes/AppRoutes'));
const AppLayout = lazy(() => import('./AppLayout'));
const AuthLayout = lazy(() => import('./AuthLayout'));

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const Layouts = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCurrentTheme = useZustand('theme', 'currentTheme');
  const storedWatermark = useZustand('theme', 'watermark');
  const storedUser = useZustand('auth', 'user');
  const storedToken = useZustand('auth', 'token');
  const blankLayout = useZustand('theme', 'blankLayout');
  const quote = useZustand('theme', 'quote');
  const quoteLoading = useZustand('theme', 'quoteLoading');
  const quoteDone = useZustand('theme', 'quoteDone');
  const fetchQuote = useZustand('theme', 'fetchQuote');
  const serverInfo = useZustand('theme', 'serverInfo');
  const serverInfoLoading = useZustand('theme', 'serverInfoLoading');
  const fetchServerVersion = useZustand('theme', 'fetchServerVersion');

  // Subscribe to language changes to force re-render of all child components
  useContext(languageContextReload);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const effectRan = useRef(false);
  const autoLoginRan = useRef(false);

  // Computed once on mount: detect if user landed on /auth/register?hash=...
  // Must use a ref (not derived state) to avoid recalculation after SignUp cleans the URL
  // via window.history.replaceState — which would flip the value mid-render and cause
  // AppRoutes to render instead of AuthRoutes, unmounting SignUp unexpectedly.
  const isRegistrationLinkAccess = useRef(
    window.location.pathname === '/auth/register' &&
      !!readInviteHashFromLocation(window.location)
  );

  // Capture the initial URL search string at mount time (before React Router's <Navigate>
  // can synchronously change window.location during the first render). Effects run AFTER
  // render, so by the time effect 5.2 runs, window.location may already point to
  // /auth/login — making readInviteHashFromLocation return null and losing the hash.
  const initialLocationRef = useRef({
    search: window.location.search,
    hash: window.location.hash,
  });

  // Capture the token that existed at mount time (before any registration/login in this session).
  // Used to distinguish a pre-existing session (must sign out) from a fresh token obtained
  // after the user completes registration (must NOT sign out).
  const initialTokenRef = useRef(storedToken);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const Layout = storedToken && !blankLayout ? AppLayout : AuthLayout;

  // Whether AppRoutes should be blocked in favour of AuthRoutes.
  // Only block when there was a pre-existing session AND the user landed via a
  // registration link — prevents RoutesIndex wildcard from redirecting to dashboard
  // before the forceLogout effect runs.
  // After registration completes (fresh token, no initial token), this is false so
  // AppRoutes renders and the user reaches the dashboard normally.
  const blockAppRoutes = isRegistrationLinkAccess.current && !!initialTokenRef.current;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const forceLogoutProcessedRef = useRef(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I - FORCE LOGOUT FROM INVITE HASH
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Sign out pre-existing sessions when a registration link is opened.
    // Guard: only run when there was a token at mount time (initialTokenRef) — this
    // prevents logging out the user AFTER they complete registration (storedToken goes
    // null → new-token and the effect would fire again without this guard).
    if (!storedToken || forceLogoutProcessedRef.current) return;
    if (!isRegistrationLinkAccess.current) return;
    if (!initialTokenRef.current) return;

    forceLogoutProcessedRef.current = true;

    // skipNavigation: true keeps the URL at /auth/register so AuthRoutes can render it
    authService.signOut({ skipNavigation: true }).catch(err => {
      console.error('[LayoutIndex] Error signing out for registration link:', err);
      forceLogoutProcessedRef.current = false;
    });
  }, [storedToken]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5.2 EFFECTS II - SAVE REDIRECT AFTER LOGIN (when NOT logged in)
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Only process when NOT logged in
    if (storedToken) return;

    // Use the initial URL captured at mount time — NOT window.location.
    // React Router's <Navigate> fires synchronously during the first render and
    // calls history.replaceState, changing window.location to /auth/login before
    // this effect runs. Reading the initial search string avoids the race condition.
    const hasHash = !!readInviteHashFromLocation(initialLocationRef.current);

    if (!hasHash) return;

    (async () => {
      try {
        const { ok, payload } = await loadAndValidateInvite(initialLocationRef.current);

        if (ok && payload?.redirectAfterLogin) {
          setStorageAuthItem('redirectAfterLogin', payload.redirectAfterLogin);
        }
      } catch (err) {
        console.error('[LayoutIndex] Error processing redirectAfterLogin:', err);
      }
    })();
  }, [storedToken]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5.3 EFFECTS III - RESET ON LOGOUT
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // ─────────────────────────────────────────────────────────────────────────
    // Reset refs when user logs out (token becomes null)
    // This ensures quotes and auto-login run again when user returns to login
    // ─────────────────────────────────────────────────────────────────────────
    if (!storedToken) {
      if (effectRan.current) {
        effectRan.current = false;
      }
      if (autoLoginRan.current) autoLoginRan.current = false;
    }
  }, [storedToken]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5.3 EFFECTS IV - APP INITIALIZATION
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // ─────────────────────────────────────────────────────────────────────────
    // Initialize app state and fetch necessary data
    // Always fetch quote when logged out (!storedToken) to ensure login screen has a quote
    // ─────────────────────────────────────────────────────────────────────────

    // Check if we're in the middle of a logout process
    // If we have storedUser, it means we're still logging out (user not cleared yet)
    // Don't fetch quote while logout is in progress to avoid duplicate requests
    const isLoggingOut = !storedToken && storedUser;

    if (isLoggingOut) {
      return;
    }

    // When logged out, always fetch quote if missing (bypass effectRan check)
    // Only fetch if not already loading to prevent duplicate requests
    if (!storedToken && !quote && !quoteLoading && !quoteDone) {
      fetchQuote();
    }

    // For other initializations, respect effectRan flag
    if (effectRan.current) return;

    if (!quote && !quoteLoading && !quoteDone) {
      fetchQuote();
    }
    if (!serverInfo && !serverInfoLoading) {
      fetchServerVersion();
    }

    effectRan.current = true;
  }, [
    fetchQuote,
    fetchServerVersion,
    storedToken,
    storedUser,
    quote,
    quoteLoading,
    serverInfo,
    serverInfoLoading,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5.4 EFFECTS V - AUTO-LOGIN ON PAGE LOAD/REFRESH
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // ─────────────────────────────────────────────────────────────────────────
    // Check localStorage for storedToken and auto-login if found
    // This handles: page refresh, browser reopened, direct URL access
    // ─────────────────────────────────────────────────────────────────────────
    if (autoLoginRan.current) return;

    // Only auto-login if:
    // 1. Token exists (loaded from localStorage by zustand)
    // 2. User data not loaded yet (need to fetch from server)
    // 3. Haven't tried auto-login yet this session
    if (storedToken && !storedUser) {
      // Mark as ran IMMEDIATELY to prevent duplicate calls during async execution
      autoLoginRan.current = true;

      (async () => {
        try {
          await authService.signInRefresh();
        } catch (err) {
          console.error('[LayoutIndex] Auto-login error:', err);
          // Keep autoLoginRan = true even on error to avoid retry loops
        }
      })();
    }
  }, [storedToken, storedUser]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Suspense fallback={null}>
      {/* Show AppRoutes only when authenticated AND NOT accessing a registration invite link.
          If the user has a stored token and opens /auth/register?hash=..., we must render
          AuthRoutes immediately — otherwise React Router's wildcard <Navigate> inside
          RoutesIndex fires synchronously during render (before any useEffect), changing the
          URL to the dashboard before the forceLogout effect can detect the register path. */}
      {storedToken && !blockAppRoutes ? (
        <Watermark
          content={storedWatermark?.content}
          font={{
            color: storedCurrentTheme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
          }}
          zIndex={0}
        >
          <Layout>
            <div
              style={{
                position: 'relative',
                minHeight: '100%',
                zIndex: 1,
                // background: 'yellow',
              }}
            >
              <SimpleLoadingModule />
              <Suspense fallback={null}>
                <SimpleLoadingPage />
                <AppRoutes />
              </Suspense>
            </div>
          </Layout>
        </Watermark>
      ) : (
        <Layout>
          <div style={{ position: 'relative', minHeight: '100%' }}>
            <SimpleLoadingModule />
            <Suspense fallback={null}>
              <SimpleLoadingPage />
              <AuthRoutes />
            </Suspense>
          </div>
        </Layout>
      )}
    </Suspense>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default memo(Layouts);
